import { useCallback, useEffect, useRef, useState } from "react";
import { Sparkles } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { apiGet } from "@/lib/api";

/**
 * #548 — Read-only view of the *configured* AI enrichment provider/model and
 * enabled-state for this environment. This reflects configuration only, not a
 * live runtime health probe: it does not call OpenRouter or verify that analysis
 * actually succeeds. Operators use it to confirm which model is wired up and
 * to spot disabled/misconfigured states before assuming AI analysis is active.
 */

type EnrichmentStatus = "enabled" | "disabled" | "misconfigured";

interface EnrichmentConfig {
  provider: string;
  model_id: string;
  enabled: boolean;
  region: string | null;
  status: EnrichmentStatus;
}

const STATUS_META: Record<
  EnrichmentStatus,
  { label: string; variant: "low" | "medium" | "secondary"; hint: string }
> = {
  enabled: {
    label: "Enabled",
    variant: "low",
    hint: "AI enrichment is configured to use the model below.",
  },
  disabled: {
    label: "Disabled",
    variant: "secondary",
    hint: "AI enrichment is not configured for this environment. Alerts are delivered without AI analysis.",
  },
  misconfigured: {
    label: "Misconfigured",
    variant: "medium",
    hint: "Enrichment is enabled but missing its OpenRouter model or API-key source. Set AI_MODEL (or AI_ENRICHMENT_MODEL) and an OpenRouter API key secret.",
  },
};

function StatusBadge({ status }: { status: EnrichmentStatus }) {
  const meta = STATUS_META[status];
  return <Badge variant={meta.variant}>{meta.label}</Badge>;
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="font-mono text-sm">{value}</span>
    </div>
  );
}

export function EnrichmentTab() {
  const [config, setConfig] = useState<EnrichmentConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const mountedRef = useRef(true);
  // Monotonic request id: only the most recently issued request is allowed to
  // commit state, so a slow/stale fetch (StrictMode effect replay, rapid retry)
  // can never clobber a newer one's result.
  const requestSeqRef = useRef(0);

  const loadStatus = useCallback(() => {
    const seq = ++requestSeqRef.current;
    const isCurrent = () => mountedRef.current && seq === requestSeqRef.current;
    setLoading(true);
    setError(false);
    apiGet<{ enrichment: EnrichmentConfig }>("/api/v1/settings/enrichment")
      .then((res) => {
        if (!isCurrent()) return;
        setConfig(res.enrichment);
      })
      .catch(() => {
        if (!isCurrent()) return;
        setError(true);
      })
      .finally(() => {
        if (isCurrent()) setLoading(false);
      });
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    loadStatus();
    return () => {
      mountedRef.current = false;
    };
  }, [loadStatus]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <CardTitle>AI Enrichment</CardTitle>
        </div>
        <CardDescription>
          Configured provider and model used to enrich alerts with AI analysis.
          Shows configured status only — not a live health check.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-3" data-testid="enrichment-loading">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-48" />
          </div>
        ) : error ? (
          <div className="space-y-3">
            <p className="text-sm text-destructive" role="alert">
              Could not load enrichment status.
            </p>
            <Button variant="outline" size="sm" onClick={loadStatus}>
              Try again
            </Button>
          </div>
        ) : config ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <StatusBadge status={config.status} />
              <span className="text-sm text-muted-foreground">
                {STATUS_META[config.status].hint}
              </span>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Field label="Provider" value={config.provider} />
              <Field label="Model" value={config.model_id} />
              <Field label="Region" value={config.region ?? "—"} />
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
